import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Loader2, RotateCcw, Shuffle,
  AlignLeft, Minus, MessageCircle, FileText, Bold, Maximize2,
  Bookmark, BookmarkCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Preset } from "@/lib/studio-presets";

interface StudioRefineControlsProps {
  refining: string | null;
  isGenerating: boolean;
  handleRefine: (action: string) => void;
  output: string;
  selectedPreset: Preset | null;
  setRemixSourceText: (t: string | null) => void;
  setRemixSourceFormat: (f: string) => void;
  setRemixOpen: (v: boolean) => void;
  studioUndoStack: string[];
  handleStudioUndo: () => void;
  saved: boolean;
  isSaving: boolean;
  handleSaveToVault: () => void;
}

export function StudioRefineControls({
  refining,
  isGenerating,
  handleRefine,
  output,
  selectedPreset,
  setRemixSourceText,
  setRemixSourceFormat,
  setRemixOpen,
  studioUndoStack,
  handleStudioUndo,
  saved,
  isSaving,
  handleSaveToVault,
}: StudioRefineControlsProps) {
  return (
    <>
      {/* Refinement controls */}
      <div className="px-4 pb-3 border-t pt-3"
        style={{ borderColor: "rgba(6,182,212,0.12)" }}
      >
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-50" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-500" />
            </span>
            <p className="text-[10px] font-bold text-cyan-400/70 uppercase tracking-widest">Quick adjust</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setRemixSourceText(output);
                setRemixSourceFormat(selectedPreset?.label ?? "custom");
                setRemixOpen(true);
              }}
              disabled={!!refining || isGenerating}
              className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/50 hover:text-cyan-400 border border-border/30 hover:border-cyan-500/30 rounded-lg px-2 py-0.5 transition-all duration-150 disabled:opacity-30"
            >
              <Shuffle className="w-2.5 h-2.5" />
              Remix
            </button>
            <AnimatePresence>
              {studioUndoStack.length > 0 && (
                <motion.button
                  data-testid="studio-undo-btn"
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  type="button"
                  onClick={handleStudioUndo}
                  disabled={!!refining}
                  className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors disabled:opacity-30"
                >
                  <RotateCcw className="w-2.5 h-2.5" />
                  Undo{studioUndoStack.length > 1 ? ` (${studioUndoStack.length})` : ""}
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Length row */}
        <p className="text-[9px] text-muted-foreground/35 uppercase tracking-widest font-bold mb-1.5">Length</p>
        <div className="flex gap-1.5 mb-3">
          {[
            { key: "shorter", label: "Shorter", icon: Minus },
            { key: "medium",  label: "Medium",  icon: AlignLeft },
            { key: "longer",  label: "Longer",  icon: Maximize2 },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleRefine(key)}
              disabled={!!refining || isGenerating}
              className={`
                flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] font-semibold
                transition-all duration-150 whitespace-nowrap disabled:opacity-40
                ${refining === key
                  ? "border-cyan-500/60 bg-cyan-500/12 text-cyan-300"
                  : "border-border/40 bg-secondary/20 text-muted-foreground hover:border-cyan-500/30 hover:bg-cyan-500/6 hover:text-cyan-300"
                }
              `}
              style={refining === key ? { boxShadow: "0 0 10px rgba(6,182,212,0.2)" } : undefined}
            >
              {refining === key ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Icon className="w-2.5 h-2.5" />}
              {label}
            </button>
          ))}
        </div>

        {/* Tone row */}
        <p className="text-[9px] text-muted-foreground/35 uppercase tracking-widest font-bold mb-1.5">Tone</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {[
            { key: "more-casual",   label: "More casual",   icon: MessageCircle },
            { key: "more-formal",   label: "More formal",   icon: FileText },
            { key: "more-bold",     label: "More bold",     icon: Bold },
            { key: "funnier",       label: "Funnier",       icon: Sparkles },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleRefine(key)}
              disabled={!!refining || isGenerating}
              className={`
                inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-medium
                transition-all duration-150 whitespace-nowrap disabled:opacity-40
                ${refining === key
                  ? "border-cyan-500/60 bg-cyan-500/12 text-cyan-300"
                  : "border-border/40 bg-secondary/20 text-muted-foreground hover:border-cyan-500/30 hover:bg-cyan-500/6 hover:text-cyan-300"
                }
              `}
              style={refining === key ? { boxShadow: "0 0 10px rgba(6,182,212,0.2)" } : undefined}
            >
              {refining === key ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Icon className="w-2.5 h-2.5" />}
              {label}
            </button>
          ))}
        </div>

        {/* Structure row */}
        <p className="text-[9px] text-muted-foreground/35 uppercase tracking-widest font-bold mb-1.5">Structure</p>
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: "punchier-opening",   label: "Punchier opening"   },
            { key: "simpler-language",   label: "Simpler language"   },
            { key: "add-examples",       label: "Add examples"       },
            { key: "add-hook",           label: "Add a hook"         },
            { key: "tighten-up",         label: "Tighten it up"      },
            { key: "remove-jargon",      label: "Remove jargon"      },
            { key: "more-descriptive",   label: "More descriptive"   },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleRefine(key)}
              disabled={!!refining || isGenerating}
              className={`
                inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-medium
                transition-all duration-150 whitespace-nowrap disabled:opacity-40
                ${refining === key
                  ? "border-cyan-500/60 bg-cyan-500/12 text-cyan-300"
                  : "border-border/40 bg-secondary/20 text-muted-foreground hover:border-cyan-500/30 hover:bg-cyan-500/6 hover:text-cyan-300"
                }
              `}
              style={refining === key ? { boxShadow: "0 0 10px rgba(6,182,212,0.2)" } : undefined}
            >
              {refining === key && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {!saved ? (
        <div className="mx-4 mb-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
          <p className="text-xs text-primary font-medium mb-2 flex items-center gap-1.5">
            <Bookmark className="w-3.5 h-3.5" /> Save this to your Idea Vault for later
          </p>
          <Button
            size="sm"
            className="w-full rounded-lg h-9 bg-primary hover:bg-primary/90"
            onClick={handleSaveToVault}
            disabled={isSaving}
          >
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Bookmark className="w-4 h-4 mr-2" /> Save to Vault</>
            )}
          </Button>
        </div>
      ) : (
        <div className="mx-4 mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2">
          <BookmarkCheck className="w-4 h-4 text-emerald-400" />
          <p className="text-sm text-emerald-400 font-medium">Saved to your Idea Vault</p>
        </div>
      )}
    </>
  );
}
